import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import api from "../../api";

const Users = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 15;

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const fetchUsers = async (pageToLoad = 1, append = false, search = "") => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      setError("");

      const response = await api.get("/admin/users", {
        params: {
          page: pageToLoad,
          pageSize,
          search: search || undefined,
        },
      });

      const newUsers = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? [];

      setUsers((prev) => (append ? [...prev, ...newUsers] : newUsers));
      setPage(pageToLoad);
      setHasMore(newUsers.length === pageSize);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des utilisateurs.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchUsers(1, false, searchTerm);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchUsers(page + 1, true, searchTerm);
  };

  const handleEdit = (id) => {
    navigate(`/dashboard/users/edit/${id}`);
  };

  const handleDelete = async (id) => {
    if (currentUser?.id === id) {
      setError("Tu ne peux pas supprimer ton propre compte.");
      return;
    }

    if (!window.confirm("Es-tu sûr de vouloir supprimer cet utilisateur ?")) {
      return;
    }

    setDeletingId(id);
    setError("");

    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((prev) => prev.filter((user) => user.id !== id));
    } catch (err) {
      console.error("Erreur lors de la suppression", err);

      setError(
        err.response?.data?.message ||
          err.response?.data?.title ||
          "Erreur lors de la suppression de l'utilisateur."
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <div className="container mx-auto px-4 py-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-lg">
            <p className="text-slate-300">Chargement en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto px-4 py-6">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-blue-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-blue-100">
              Dashboard utilisateurs
            </p>

            <h1 className="mb-3 text-3xl font-bold md:text-4xl">
              Gestion des utilisateurs
            </h1>

            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Gérez les comptes, recherchez des utilisateurs et modifiez leurs
              informations.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-lg">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3 md:flex-row"
            >
              <input
                type="text"
                placeholder="Rechercher par nom, prénom, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-2.5 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 md:w-96"
              />

              <button
                type="submit"
                className="rounded-xl bg-blue-500 px-4 py-2.5 font-semibold text-white transition hover:bg-blue-600"
              >
                Rechercher
              </button>

              <button
                type="button"
                onClick={handleResetSearch}
                className="rounded-xl bg-slate-700 px-4 py-2.5 font-semibold text-white transition hover:bg-slate-600"
              >
                Réinitialiser
              </button>
            </form>

            <button
              onClick={() => navigate("/dashboard/users/create")}
              className="rounded-xl bg-green-500 px-4 py-2.5 font-semibold text-white shadow-md transition hover:bg-green-600"
            >
              Créer un utilisateur
            </button>
          </div>

          {searchTerm && (
            <p className="mb-4 text-sm text-slate-400">
              Résultats pour{" "}
              <span className="font-semibold text-slate-200">
                "{searchTerm}"
              </span>
            </p>
          )}

          {error && (
            <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="min-w-full bg-slate-900">
              <thead>
                <tr className="bg-slate-800 text-slate-200">
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Avatar
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Nom
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Email
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Localité
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Rôle
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Admin
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Commentaires
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Inscrit le
                  </th>
                  <th className="border border-slate-700 px-4 py-3 text-left font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {users.length > 0 ? (
                  users.map((user) => {
                    const avatarUrl = getImageUrl(user.avatar);

                    return (
                      <tr
                        key={user.id}
                        className="transition hover:bg-slate-800/60"
                      >
                        <td className="border border-slate-800 px-4 py-3 align-middle">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={`${user.firstname} ${user.lastname}`}
                              onClick={() => setSelectedAvatar(avatarUrl)}
                              className="h-10 w-10 cursor-pointer rounded-full border border-slate-700 object-cover transition hover:scale-110"
                            />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 bg-slate-800 text-xs text-slate-400">
                              —
                            </div>
                          )}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle">
                          <p className="font-semibold text-white">
                            {user.lastname || "—"} {user.firstname || ""}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            #{user.id}
                          </p>
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle text-slate-300">
                          {user.email}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle text-slate-400">
                          {user.location || "—"}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle">
                          {user.role ? (
                            <span className="inline-flex rounded-full bg-blue-500/10 px-3 py-1 text-xs font-semibold text-blue-300">
                              {user.role}
                            </span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle">
                          {user.isAdmin ? (
                            <span className="inline-flex rounded-full bg-green-500/10 px-3 py-1 text-xs font-semibold text-green-300">
                              Oui
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-700/50 px-3 py-1 text-xs text-slate-400">
                              Non
                            </span>
                          )}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle text-center text-slate-300">
                          {user.commentsCount ?? 0}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle text-sm text-slate-400">
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString(
                                "fr-BE"
                              )
                            : "—"}
                        </td>

                        <td className="border border-slate-800 px-4 py-3 align-middle">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleEdit(user.id)}
                              className="rounded-lg bg-blue-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-blue-600"
                            >
                              Modifier
                            </button>

                            <button
                              onClick={() => handleDelete(user.id)}
                              disabled={
                                deletingId === user.id ||
                                currentUser?.id === user.id
                              }
                              className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                                currentUser?.id === user.id
                                  ? "cursor-not-allowed bg-slate-700 text-slate-500"
                                  : "bg-red-500 text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                              }`}
                            >
                              {deletingId === user.id
                                ? "Suppression..."
                                : "Supprimer"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="9"
                      className="border border-slate-800 px-4 py-6 text-center text-slate-400"
                    >
                      Aucun utilisateur trouvé.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {hasMore && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-orange-300"
            >
              {loadingMore ? "Chargement..." : "Charger plus"}
            </button>
          </div>
        )}
      </main>

      {selectedAvatar && (
        <div
          onClick={() => setSelectedAvatar(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
        >
          <div onClick={(e) => e.stopPropagation()} className="relative">
            <button
              onClick={() => setSelectedAvatar(null)}
              className="absolute right-3 top-3 rounded-full bg-black/50 px-3 py-1 text-sm font-bold text-white transition hover:bg-black/70"
            >
              ✕
            </button>

            <img
              src={selectedAvatar}
              alt="Avatar"
              className="max-h-[80vh] max-w-[90vw] rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;